import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Link, useNavigate } from 'react-router-dom';

// Import components
import CyberdefenseNavbar from '../components/layout/CyberdefenseNavbar';
import CyberdefenseFooter from '../components/layout/CyberdefenseFooter';
import { scrollToSection } from '../utils/scrollUtils';

// Component implementation
const CyberdefensePage = () => {
  const [scrollOpacity, setScrollOpacity] = useState(1);
  const navigate = useNavigate();

  // Scroll to top on page load
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  
  // Handle scroll effect for scroll button
  useEffect(() => {
    const handleScroll = () => {
      const opacity = Math.max(1 - window.scrollY / 400, 0);
      setScrollOpacity(opacity);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Check if mobile device
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    // Check on mount and add listener for resize
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Animation refs for different sections
  const [aboutRef, aboutInView] = useInView({
    threshold: isMobile ? 0.05 : 0.2,
    rootMargin: isMobile ? "100px" : "0px",
  });

  const [techStackRef, techStackInView] = useInView({
    threshold: isMobile ? 0.05 : 0.2,
    rootMargin: isMobile ? "100px" : "0px",
  });

  const [featuresRef, featuresInView] = useInView({
    threshold: isMobile ? 0.05 : 0.2,
    rootMargin: isMobile ? "100px" : "0px",
  });

  return (
    <LayoutWrapper>
      <CyberdefenseNavbar transparent={true} />
      <Main>
        <HeroSection>
        <HeroContent>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1>AI Cyberdefense System</h1>
            <p>Next-generation cybersecurity powered by artificial intelligence</p>
          </motion.div>
        </HeroContent>
        
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: scrollOpacity }}
          transition={{ duration: 0.3 }}
        >
          <ScrollDown onClick={() => scrollToSection('ai-intelligence')}>
            <span>Explore System</span>
            <svg 
              width="16" 
              height="24" 
              viewBox="0 0 16 24" 
              fill="none" 
              xmlns="http://www.w3.org/2000/svg"
            >
              <path 
                d="M8 0L8 20M8 20L1 13M8 20L15 13" 
                stroke="currentColor" 
                strokeWidth="2"
              />
            </svg>
          </ScrollDown>
        </motion.div>
      </HeroSection>

      <PageContent>
        <AIBlogSection id="ai-intelligence">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <BlogTitle>AI CyberSecurity Blogs</BlogTitle>
            <BlogDescription>
              AI-powered analysis and insights on cybersecurity threats, attack methods, and defensive strategies. This blog explores the latest developments in cyber intelligence and security research.
            </BlogDescription>
            
            <BlogCard>
              <BlogHeader>
                <BlogDate>Latest Update: {new Date().toLocaleDateString()}</BlogDate>
                <BlogCategory>AI Generated</BlogCategory>
              </BlogHeader>
              <BlogContent>
                <BlogPost>
                  <h4>Emerging APT Techniques: AI Analysis Report</h4>
                  <p>Our machine learning models have identified several new Advanced Persistent Threat (APT) patterns in recent network traffic. 
                  The AI analysis reveals sophisticated techniques including lateral movement strategies and novel evasion methods.</p>
                </BlogPost>
                <BlogPost>
                  <h4>Zero-Day Vulnerability Prediction</h4>
                  <p>Using pattern recognition and code analysis, the AI system has flagged potential zero-day vulnerabilities in common 
                  software frameworks, allowing for proactive security measures.</p>
                </BlogPost>
              </BlogContent>
            </BlogCard>
            
            <BlogNote>
              <strong>Note:</strong> These insights are placeholders and will be updated daily after the Intelligence Processing and Learning & Analysis stages are completed. To learn more about the project stages, visit the About section below.
            </BlogNote>
          </motion.div>
        </AIBlogSection>

        <FeaturesSection 
          id="features"
          ref={featuresRef}
          as={motion.section}
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <SectionTitle>AI-Powered Security Features</SectionTitle>
          <SectionDescription>An advanced cybersecurity platform that leverages machine learning and AI to protect against modern threats in real-time.</SectionDescription>

          <Features>
            <Feature>
              <FeatureIcon>🛡️</FeatureIcon>
              <FeatureTitle>Threat Detection</FeatureTitle>
              <FeatureDesc>Advanced ML algorithms continuously monitor network traffic and system behavior to identify potential security threats before they escalate</FeatureDesc>
            </Feature>
            
            <Feature>
              <FeatureIcon>🔍</FeatureIcon>
              <FeatureTitle>Vulnerability Scanner</FeatureTitle>
              <FeatureDesc>Automated vulnerability assessment using AI-driven penetration testing and code analysis to identify security weaknesses</FeatureDesc>
            </Feature>
            
            <Feature>
              <FeatureIcon>🧠</FeatureIcon>
              <FeatureTitle>Behavioral Analysis</FeatureTitle>
              <FeatureDesc>Machine learning models analyze user and system behavior patterns to detect anomalies and potential insider threats</FeatureDesc>
            </Feature>
            
            <Feature>
              <FeatureIcon>⚡</FeatureIcon>
              <FeatureTitle>Real-time Response</FeatureTitle>
              <FeatureDesc>Automated incident response system that can immediately isolate threats and implement countermeasures</FeatureDesc>
            </Feature>
          </Features>
        </FeaturesSection>

        <Section 
          id="about"
          ref={aboutRef}
          as={motion.section}
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <SectionTitle>About the Project</SectionTitle>
          <p>The AI Cyberdefense System represents a cutting-edge approach to cybersecurity, combining traditional security practices with artificial intelligence and machine learning. This personal project explores the intersection of AI and cybersecurity, demonstrating how advanced algorithms can enhance threat detection and response capabilities.</p>

          <SystemFlowTitle>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              System Process Pipeline
            </motion.div>
          </SystemFlowTitle>

          <SystemFlowContainer>
            {[
              { 
                id: 'data-acquisition', 
                icon: '📊', 
                title: 'Data Acquisition', 
                desc: 'Continuously monitors security feeds, CVE databases, and research publications. Ingests vulnerability reports and threat intelligence from multiple sources.', 
                status: 'Completed',
                delay: 0 
              },
              { 
                id: 'intelligence-processing', 
                icon: '🧠', 
                title: 'Intelligence Processing', 
                desc: 'Processes raw security data using specialized NLP models. Extracts entities, classifies threats, and identifies attack vectors.', 
                status: 'In Development',
                delay: 0.1 
              },
              { 
                id: 'learning-analysis', 
                icon: '🤖', 
                title: 'Learning & Analysis', 
                desc: 'Trains multiple AI models including LSTM networks for vulnerability detection and transformer models for pattern recognition.', 
                status: 'In Development',
                delay: 0.2 
              },
              { 
                id: 'controlled-testing', 
                icon: '🔬', 
                title: 'Controlled Testing', 
                desc: 'Creates isolated sandbox environments using containers. Implements strict boundary protection for safe testing execution.', 
                status: 'Not Started',
                delay: 0.3 
              },
              { 
                id: 'vulnerability-assessment', 
                icon: '🔍', 
                title: 'Vulnerability Assessment', 
                desc: 'Executes comprehensive security tests including network scanning, web application testing, and custom exploit development.', 
                status: 'Not Started',
                delay: 0.4 
              },
              { 
                id: 'countermeasure-generation', 
                icon: '🛡️', 
                title: 'Countermeasure Generation', 
                desc: 'Analyzes vulnerabilities to generate effective remediation strategies. Creates both pattern-matched and novel countermeasures.', 
                status: 'Not Started',
                delay: 0.5 
              },
              { 
                id: 'reporting-implementation', 
                icon: '📋', 
                title: 'Reporting & Implementation', 
                desc: 'Generates detailed technical reports and implementation guides. Provides secure API access through security dashboard.', 
                status: 'Not Started',
                delay: 0.6 
              }
            ].map((node, index, array) => (
              <React.Fragment key={node.id}>
                <SystemNode
                  id={node.id}
                  as={motion.div}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 20 }}
                  transition={{ duration: 0.5, delay: node.delay }}
                  whileHover={{ scale: 1.02 }}
                >
                  <SystemNodeStatus status={node.status}>{node.status}</SystemNodeStatus>
                  <SystemNodeIcon>{node.icon}</SystemNodeIcon>
                  <div>
                    <SystemNodeTitle>{node.title}</SystemNodeTitle>
                    <SystemNodeDesc>{node.desc}</SystemNodeDesc>
                  </div>
                </SystemNode>
                {index < array.length - 1 && (
                  <SystemNodeArrow
                    as={motion.div}
                    initial={{ opacity: 0, scale: 0.5 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.3, delay: node.delay + 0.3 }}
                  >
                    ↓
                  </SystemNodeArrow>
                )}
              </React.Fragment>
            ))}
          </SystemFlowContainer>

          <SubsectionTitle>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              Technology Stack
            </motion.div>
          </SubsectionTitle>
          
          <TechGrid>
            {[
              { name: 'Python Security', desc: 'Core security framework with penetration testing tools', delay: 0 },
              { name: 'Machine Learning', desc: 'TensorFlow and Scikit-learn for threat detection', delay: 0.1 },
              { name: 'Docker Containers', desc: 'Secure, isolated deployment environments', delay: 0.2 },
              { name: 'Network Analysis', desc: 'Real-time traffic monitoring and analysis', delay: 0.3 },
              { name: 'NLP Processing', desc: 'Natural language processing for threat intelligence', delay: 0.4 },
              { name: 'Database Security', desc: 'Encrypted data storage and access control', delay: 0.5 }
            ].map((tech, index) => (
              <TechItem 
                key={index}
                as={motion.div}
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: tech.delay }}
              >
                <TechName>{tech.name}</TechName>
                <TechDescription>{tech.desc}</TechDescription>
              </TechItem>
            ))}
          </TechGrid>
        </Section>

        <ProjectLinks>
          <Button primary onClick={() => {
            navigate('/');
            setTimeout(() => {
              scrollToSection('projects');
            }, 50);
          }}>Back to Projects</Button>
        </ProjectLinks>
      </PageContent>
      </Main>
      <CyberdefenseFooter />
    </LayoutWrapper>
  );
};

export default CyberdefensePage;

// Custom Layout Components
const LayoutWrapper = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
`;

const Main = styled.main`
  flex: 1;
  background-color: ${({ theme }) => theme.colors.background};
  min-height: 100vh;
  width: 100%;
  overflow-x: hidden;
`;

// Styled Components
// Base Layout Components
const HeroSection = styled.section`
  position: relative;
  height: 100vh;
  width: 100%;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  background: linear-gradient(135deg, #000000 0%, #1a0033 50%, #330066 100%);
`;

const HeroContent = styled.div`
  text-align: center;
  max-width: 800px;
  padding: 0 2rem;
  
  h1 {
    font-size: 4rem;
    margin-bottom: 1rem;
    background: linear-gradient(to right, #FFFFFF, #9d4edd, #c77dff);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    color: transparent;
    
    @media (max-width: 768px) {
      font-size: 2.5rem;
    }
  }
  
  p {
    font-size: 1.5rem;
    margin-bottom: 2rem;
    opacity: 0.9;
    
    @media (max-width: 768px) {
      font-size: 1.2rem;
    }
  }
`;

const ScrollDown = styled.button`
  position: absolute;
  bottom: 2rem;
  left: 50%;
  transform: translateX(-50%);
  background: none;
  border: none;
  color: white;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9rem;
  opacity: 0.8;
  transition: all 0.3s ease;
  
  &:hover {
    opacity: 1;
    color: #c084fc;
  }
  
  svg {
    animation: bounce 2s infinite;
  }
  
  @keyframes bounce {
    0%, 20%, 50%, 80%, 100% {
      transform: translateY(0);
    }
    40% {
      transform: translateY(-10px);
    }
    60% {
      transform: translateY(-5px);
    }
  }
`;

const PageContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const Section = styled.section`
  padding: 4rem 0 2rem 0;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: 3rem 0 1rem 0;
  }
`;

const FeaturesSection = styled.section`
  padding: 4rem 0 2rem 0;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: 3rem 0 1rem 0;
  }
`;

const SectionTitle = styled.h2`
  font-size: 2.5rem;
  margin-bottom: 2rem;
  text-align: center;
  color: ${({ theme }) => theme.colors.text};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: 2rem;
  }
`;

const SectionDescription = styled.p`
  text-align: center;
  font-size: 1rem;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: 2rem;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.6;
`;

const Features = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 2rem;
  margin: 3rem 0;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    grid-template-columns: 1fr;
  }
`;

const Feature = styled.div`
  padding: 2rem;
  background: rgba(157, 78, 221, 0.05);
  border-radius: 12px;
  border: 1px solid rgba(157, 78, 221, 0.1);
  text-align: center;
`;

const FeatureIcon = styled.div`
  font-size: 2rem;
  margin-bottom: 1rem;
`;

const FeatureTitle = styled.h4`
  font-size: 1.25rem;
  margin-bottom: 1rem;
  color: ${({ theme }) => theme.colors.text};
`;

const FeatureDesc = styled.p`
  font-size: 0.95rem;
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

// System Flow Components
const SystemFlowTitle = styled.h3`
  font-size: 1.5rem;
  margin: 3rem 0 2rem;
  text-align: center;
  color: ${({ theme }) => theme.colors.text};
`;

const SystemFlowContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  padding: 2rem 0;
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    gap: 0.5rem;
    padding: 1rem;
  }
`;

const SystemNode = styled(motion.div)<{ id: string }>`
  position: relative;
  background: rgba(157, 78, 221, 0.1);
  border: 2px solid rgba(157, 78, 221, 0.2);
  border-radius: 16px;
  text-align: left;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  display: flex;
  flex-direction: row;
  align-items: center;
  width: 100%;
  min-height: 120px;
  padding: 1.5rem 3rem;
  gap: 2rem;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 20px 40px rgba(157, 78, 221, 0.3);
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    min-height: 100px;
    padding: 1rem 1.5rem;
    gap: 1rem;
    flex-direction: column;
    text-align: center;
  }
`;

const SystemNodeIcon = styled.div`
  font-size: 3rem;
  flex-shrink: 0;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: 2.5rem;
    margin-bottom: 0.5rem;
  }
`;

const SystemNodeTitle = styled.h4`
  font-size: 1.3rem;
  margin-bottom: 0.5rem;
  color: ${({ theme }) => theme.colors.text};
  font-weight: 600;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: 1.2rem;
    margin-bottom: 0.3rem;
  }
`;

const SystemNodeDesc = styled.p`
  font-size: 0.95rem;
  color: ${({ theme }) => theme.colors.textSecondary};
  line-height: 1.5;
  margin: 0;
  overflow-wrap: break-word;
  hyphens: auto;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    text-align: center;
    font-size: 0.9rem;
  }
`;

const SystemNodeArrow = styled.div`
  font-size: 2rem;
  color: #9d4edd;
  opacity: 0.8;
  pointer-events: none;
  text-align: center;
  margin: 0.25rem 0;
  
  &:hover {
    opacity: 1;
  }
`;

const SystemNodeStatus = styled.div<{ status: string }>`
  position: absolute;
  top: 1rem;
  right: 1rem;
  padding: 0.4rem 0.8rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  background: ${props => {
    switch (props.status) {
      case 'Completed':
        return 'rgba(48, 209, 88, 0.2)';
      case 'In Development':
        return 'rgba(255, 214, 10, 0.2)';
      case 'Not Started':
        return 'rgba(255, 69, 58, 0.2)';
      default:
        return 'rgba(142, 142, 147, 0.2)';
    }
  }};
  color: ${props => {
    switch (props.status) {
      case 'Completed':
        return '#30d158';
      case 'In Development':
        return '#ffd60a';
      case 'Not Started':
        return '#ff453a';
      default:
        return '#8e8e93';
    }
  }};
  border: 1px solid ${props => {
    switch (props.status) {
      case 'Completed':
        return 'rgba(48, 209, 88, 0.3)';
      case 'In Development':
        return 'rgba(255, 214, 10, 0.3)';
      case 'Not Started':
        return 'rgba(255, 69, 58, 0.3)';
      default:
        return 'rgba(142, 142, 147, 0.3)';
    }
  }};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    top: 0.75rem;
    right: 0.75rem;
    padding: 0.3rem 0.6rem;
    font-size: 0.7rem;
  }
`;

// AI Blog Section Components
const AIBlogSection = styled.section`
  padding: 4rem 0 2rem 0;
  margin: 0;
  
  > div {
    padding: 2rem;
    background: rgba(157, 78, 221, 0.03);
    border-radius: 16px;
    border: 1px solid rgba(157, 78, 221, 0.1);
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: 2rem 0 1rem 0;
    
    > div {
      padding: 1.5rem;
    }
  }
`;

const BlogTitle = styled.h3`
  font-size: 2.5rem;
  margin-bottom: 2rem;
  text-align: center;
  color: ${({ theme }) => theme.colors.text};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: 2rem;
  }
`;

const BlogDescription = styled.p`
  font-size: 1rem;
  color: ${({ theme }) => theme.colors.textSecondary};
  text-align: center;
  margin-bottom: 2rem;
  line-height: 1.6;
`;

const BlogCard = styled.div`
  background: rgba(157, 78, 221, 0.05);
  border-radius: 12px;
  padding: 1.5rem;
  border: 1px solid rgba(157, 78, 221, 0.15);
`;

const BlogHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    flex-direction: column;
    gap: 0.5rem;
    align-items: flex-start;
  }
`;

const BlogDate = styled.span`
  font-size: 0.9rem;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const BlogCategory = styled.span`
  background: rgba(157, 78, 221, 0.2);
  color: #9d4edd;
  padding: 0.3rem 0.8rem;
  border-radius: 15px;
  font-size: 0.8rem;
  font-weight: 600;
`;

const BlogContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const BlogPost = styled.div`
  h4 {
    font-size: 1.1rem;
    margin-bottom: 0.5rem;
    color: ${({ theme }) => theme.colors.text};
  }
  
  p {
    font-size: 0.95rem;
    line-height: 1.6;
    color: ${({ theme }) => theme.colors.textSecondary};
  }
`;

const BlogNote = styled.div`
  margin-top: 1.5rem;
  padding: 1rem;
  background: rgba(157, 78, 221, 0.1);
  border-left: 4px solid #9d4edd;
  border-radius: 0 8px 8px 0;
  font-size: 0.9rem;
  color: ${({ theme }) => theme.colors.textSecondary};
  
  strong {
    color: ${({ theme }) => theme.colors.text};
  }
`;

// Tech Stack Components
const SubsectionTitle = styled.h3`
  font-size: 2rem;
  margin: 3rem 0 2rem;
  text-align: center;
  color: ${({ theme }) => theme.colors.text};
`;

const TechGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1.5rem;
  margin-top: 2rem;
`;

const TechItem = styled(motion.div)`
  padding: 1.5rem;
  background: rgba(157, 78, 221, 0.05);
  border-radius: 12px;
  border: 1px solid rgba(157, 78, 221, 0.1);
  transition: transform 0.2s ease;
  
  &:hover {
    transform: translateY(-5px);
  }
`;

const TechName = styled.h4`
  font-size: 1.1rem;
  margin-bottom: 0.5rem;
  color: ${({ theme }) => theme.colors.text};
`;

const TechDescription = styled.p`
  font-size: 0.9rem;
  color: ${({ theme }) => theme.colors.textSecondary};
  line-height: 1.5;
`;

// Navigation Components
const ProjectLinks = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
  margin: 4rem 0;
`;

const Button = styled.button<{ primary?: boolean }>`
  background: ${props => props.primary ? 
    'linear-gradient(135deg, #9d4edd, #c77dff)' : 
    'transparent'};
  color: ${props => props.primary ? 'white' : '#9d4edd'};
  border: ${props => props.primary ? 'none' : '2px solid #9d4edd'};
  padding: 1rem 2rem;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  text-decoration: none;
  display: inline-block;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 20px rgba(157, 78, 221, 0.3);
    background: ${props => props.primary ? 
      'linear-gradient(135deg, #7b2cbf, #9d4edd)' : 
      'rgba(157, 78, 221, 0.1)'};
  }
`;
